import { fetchOrdersBundles } from 'fetch/orderFetch';
import {
  createBundleDraftIsolated,
  deleteDraftBundle,
  moveOrdersMessage,
  OrderBundle,
  pickupOnTopWithGrouping,
  publishMultipleDraft,
  removeOrdersFromBundle,
  reorderBundleWithAlgorithm,
} from 'pages/oldPlanner/utils/bundleDraft';
import {
  PlannerBundle,
  PlannerOrder,
} from 'pages/planner/utils/usePlannerData';
import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { BundleJson, BundleStatus, ReorderMode } from 'types/bundleTypes';
import { Region } from 'types/orderTypes';
import { useKeyPress } from 'utils/useKeyPress';
import { FilterButton } from '../components/PlannerFilterButton';

interface BundleMenuProps {
  region: Region;
  bundles: PlannerBundle[];
  selectedDraftBundles: string[];
  selectedOrderCount: number;
  refetching: boolean;
  selectedOrders?: PlannerOrder[];
  selectedOrderIds?: string[];
  updateSelectedDraftBundles: (newSelectedDraftBundles: Array<string>) => void;
  reFetch: () => void;
}

export const BundleMenu = ({
  updateSelectedDraftBundles,
  reFetch,
  ...props
}: BundleMenuProps) => {
  const [creatingBundle, setCreatingBundle] = useState<boolean>(false);
  const [publishingBundles, setPublishingBundles] = useState<boolean>(false);
  const [deletingBundles, setDeletingBundles] = useState<boolean>(false);

  const bundleWithDraftStatus = props.bundles.filter(
    bundle => bundle.status === BundleStatus.Draft
  );
  const emptyDraftBundle = bundleWithDraftStatus.length <= 0;
  const isSelectedAllDraftBundle =
    !emptyDraftBundle &&
    bundleWithDraftStatus.length === props.selectedDraftBundles.length;

  const emptySelectedOrder = props.selectedOrderCount === 0;
  const emptySelectedDraftBundle = props.selectedDraftBundles.length === 0;

  const toggleSelectAllBundles = () => {
    if (isSelectedAllDraftBundle) {
      updateSelectedDraftBundles([]);
    } else {
      const allBundlesId = bundleWithDraftStatus.map(bundle => bundle.id);
      updateSelectedDraftBundles(allBundlesId);
    }
  };

  const autoBundleAction = () => {
    window.alert('Not implemented yet');
  };

  const createDraftBundle = async (
    orders: PlannerOrder[]
  ): Promise<BundleJson> => {
    try {
      const bundleCreated: BundleJson = await createBundleDraftIsolated(
        orders,
        props.region
      );
      await pickupOnTopWithGrouping(bundleCreated);

      return bundleCreated;
    } catch (error) {
      Swal.fire(
        'Could not create new bundle',
        (error as Error)?.message || '',
        'error'
      );
      return null;
    }
  };

  const getOrdersAlreadyInBundle = async (
    orders: string[]
  ): Promise<OrderBundle[]> => {
    const orderBundleList = await fetchOrdersBundles(orders);
    return orderBundleList.filter(ob => !!ob.bundle);
  };

  const createBundleAction = async (reorder?: boolean) => {
    if (props.refetching || creatingBundle) {
      return;
    }

    setCreatingBundle(true);

    const ordersAlreadyInBundle = await getOrdersAlreadyInBundle(
      props.selectedOrderIds
    );

    let bundle: BundleJson;
    if (ordersAlreadyInBundle.length) {
      Swal.fire({
        html: moveOrdersMessage(ordersAlreadyInBundle, null),
        title: 'Move orders to new bundle?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Move orders',
        cancelButtonText: 'Cancel',
      })
        .then(async result => {
          if (result.value) {
            await removeOrdersFromBundle(
              ordersAlreadyInBundle.map(ob => ob.orderId)
            );
            createDraftBundle(props.selectedOrders);
          }
        })
        .catch(error => {
          Swal.fire(
            'Could not move orders to the new bundle',
            error.message,
            'error'
          );
        });
    } else {
      bundle = await createDraftBundle(props.selectedOrders);
    }

    if (bundle && reorder) {
      await reorderBundleWithAlgorithm(
        ReorderMode.OpenRoute,
        bundle.id,
        () => {
          /* Finished reorder */
        },
        error => {
          Swal.fire({
            title: `Something unexpected occurred when re-ordering bundle. Error response: ${JSON.stringify(
              error
            )}`,
            icon: 'error',
          });
        }
      );
    }
    setCreatingBundle(false);
    reFetch();
  };

  const createBundle = () => {
    createBundleAction();
  };

  const createBundleAndReorder = () => {
    createBundleAction(true);
  };

  const publishSelectedDraftBundles = async () => {
    setPublishingBundles(true);

    try {
      await publishMultipleDraft(props.selectedDraftBundles);

      updateSelectedDraftBundles([]);
      reFetch();
    } catch (error: unknown) {
      Swal.fire(
        'Something unexpected occurred when publishing the bundles',
        (error as Error)?.message || '',
        'error'
      );
    }
    setPublishingBundles(false);
  };

  const deleteSelectedDraftBundles = async () => {
    const draftBundleList = props.selectedDraftBundles.map(
      bundle => `<li><strong>${bundle}</strong></li>`
    );
    const confirmationMessage = `
      <p>Selected draft bundles :</p>
      <ul>${draftBundleList}</li>
      <p>Are you sure you want to delete the drafts?
    `;

    const result = await Swal.fire({
      title: 'Danger zone!',
      html: confirmationMessage,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete drafts',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#ccc',
    });
    if (result.value) {
      setDeletingBundles(true);

      let hasFailedDeletion = false;
      const requests = props.selectedDraftBundles.map(async bundleDraftId => {
        try {
          await deleteDraftBundle(bundleDraftId);
        } catch {
          hasFailedDeletion = true;
        }
      });
      await Promise.all(requests);

      if (hasFailedDeletion) {
        alert('Something wrong happened');
      }
      updateSelectedDraftBundles([]);
      setDeletingBundles(false);
      reFetch();
    }
  };

  useKeyPress('c', {
    keyupCallback: () => {
      if (!emptySelectedOrder) {
        createBundleAction(true);
      }
    },
  });

  return (
    <div className="button-group">
      <FilterButton
        title={isSelectedAllDraftBundle ? 'Unselect all' : 'Select all'}
        disabled={emptyDraftBundle}
        onClick={toggleSelectAllBundles}
      />

      <FilterButton
        onClick={autoBundleAction}
        title={
          emptySelectedOrder
            ? 'Auto bundle all'
            : `Auto bundle ${props.selectedOrderCount}`
        }
      />
      <FilterButton
        disabled={emptySelectedOrder}
        loading={creatingBundle}
        onClick={createBundle}
        title="Create"
      />
      <FilterButton
        disabled={emptySelectedOrder}
        onClick={createBundleAndReorder}
        hoverTitle="Press (c) to create bundle"
        title="Create & Reorder"
        loading={creatingBundle}
      />
      <FilterButton
        disabled={emptySelectedDraftBundle}
        loading={publishingBundles}
        style={
          emptySelectedDraftBundle
            ? {}
            : { backgroundColor: '#72b36e', color: 'white' }
        }
        onClick={publishSelectedDraftBundles}
        title="Publish"
      />

      <FilterButton
        disabled={emptySelectedDraftBundle}
        loading={deletingBundles}
        style={
          emptySelectedDraftBundle
            ? {}
            : { backgroundColor: '#dd4e50', color: 'white' }
        }
        onClick={deleteSelectedDraftBundles}
        title="Delete"
      />
    </div>
  );
};
